import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ApiErrorState } from '../components/ApiErrorState.tsx';
import { Cargando } from '../components/FullScreenMessage.tsx';
import { NotFoundPage } from './NotFoundPage.tsx';
import { formatDate, machineDateTime } from '../lib/datetime.ts';
import { useAuth } from '../features/auth/useAuth.ts';
import { useLeagueDetail, useLeaveLeague } from '../features/leagues/hooks.ts';
import { LeaveLeagueDialog } from '../features/leagues/LeaveLeagueDialog.tsx';

export function LeagueDetailPage() {
  const { t, i18n } = useTranslation();
  const { leagueId } = useParams<{ leagueId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [confirmando, setConfirmando] = useState(false);

  const { data, isPending, error } = useLeagueDetail(leagueId ?? '');
  const salir = useLeaveLeague();

  if (!leagueId) return <NotFoundPage />;
  if (isPending) return <Cargando text={t('common.loading')} />;
  // Aquí cae el 404 de privacidad: liga inexistente o de la que no soy miembro. El
  // backend no distingue los dos casos a propósito (ADR 0004) y la interfaz tampoco.
  if (error) return <ApiErrorState error={error} />;
  if (!data) return <NotFoundPage />;

  const { league, members } = data;
  const soyAdmin = league.role === 'owner';
  const miembrosActivos = members.length;

  async function confirmarSalida() {
    await salir.mutateAsync(leagueId!);
    void navigate('/', { replace: true, state: { leftLeague: league.name } });
  }

  return (
    <>
      <header className="mb-4">
        <h1 className="text-xl font-bold" data-test="nombre-liga">
          {league.name}
        </h1>
        <p className="text-sm text-tenue">
          {league.competition_name} · {league.season_label}
        </p>
      </header>

      <nav className="mb-4 flex gap-2">
        <Link
          to={`/leagues/${league.id}/predictions`}
          className="btn-secundario flex-1 no-underline"
          data-test="ir-pronosticos"
        >
          {t('leagues.detail.predictions')}
        </Link>
        <Link
          to={`/leagues/${league.id}/standings`}
          className="btn-secundario flex-1 no-underline"
          data-test="ir-clasificacion"
        >
          {t('leagues.detail.standings')}
        </Link>
      </nav>


      <section className="tarjeta mb-4">
        <h2 className="mb-1 text-sm font-semibold text-tenue">{t('leagues.detail.inviteCode')}</h2>
        <p
          className="font-mono text-2xl font-bold tracking-[0.2em] text-acento"
          data-test="codigo-invitacion"
        >
          {league.invite_code}
        </p>
        <p className="mt-1 text-xs text-tenue">{t('leagues.detail.inviteCodeHint')}</p>
      </section>

      <section className="tarjeta mb-4">
        <h2 className="mb-3 font-bold">
          {t('leagues.detail.members')}{' '}
          <span className="font-normal text-tenue">({miembrosActivos})</span>
        </h2>
        <ul className="list-none space-y-2 p-0" data-test="lista-miembros">
          {members.map((member) => (
            <li
              key={member.user_id}
              className="flex items-center justify-between gap-3 border-b border-borde pb-2 last:border-0 last:pb-0"
              data-test="miembro"
              data-username={member.username}
              data-role={member.role}
            >
              <span className="min-w-0">
                <span className="truncate font-medium">{member.username}</span>
                {member.user_id === user?.id ? (
                  <span className="ml-1 text-xs text-tenue">({t('leagues.detail.you')})</span>
                ) : null}
                <time
                  className="block text-xs text-tenue"
                  dateTime={machineDateTime(member.joined_at)}
                >
                  {t('leagues.detail.joinedOn', {
                    date: formatDate(member.joined_at, i18n.language),
                  })}
                </time>
              </span>
              <span
                className={
                  member.role === 'owner'
                    ? 'shrink-0 rounded-full border border-acento px-2 py-0.5 text-xs font-semibold text-acento'
                    : 'shrink-0 text-xs text-tenue'
                }
              >
                {member.role === 'owner' ? t('leagues.detail.admin') : t('leagues.detail.member')}
              </span>
            </li>
          ))}
        </ul>
        {/*
          Sin acciones de administración: expulsar miembros y transferir el rol a mano
          NO existen en el backend (ADR 0004, backlog explícito). Enseñar botones que
          no hacen nada sería peor que no enseñar ninguno.
        */}
      </section>

      <button
        type="button"
        className="btn-peligro w-full"
        onClick={() => setConfirmando(true)}
        data-test="abrir-abandonar"
      >
        {t('leagues.detail.leave')}
      </button>

      {confirmando ? (
        <LeaveLeagueDialog
          esAdmin={soyAdmin}
          esUltimoMiembro={miembrosActivos === 1}
          pendiente={salir.isPending}
          error={salir.error}
          onConfirmar={confirmarSalida}
          onCancelar={() => setConfirmando(false)}
        />
      ) : null}
    </>
  );
}
